import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import Logo from "../../assets/logo.jpeg";
import api from "../../services/api";

export default function Login() {
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Ekdum clean Axios call
      const response = await api.post("/admin/login", 
        { 
          email: form.email,
          password: form.password
        }
      );

      // Agar login success hua (Status 200)
      if (response.status === 200) {
        localStorage.setItem("admin_token", response.data.token);
        localStorage.setItem("admin", JSON.stringify(response.data.admin));
        navigate("/dashboard"); // Seedha dashboard par bhej do
      }
      
    } catch (error) {
      // Agar email/password galat hua ya koi aur error aayi
      if (error.response && error.response.data && error.response.data.message) {
        alert(error.response.data.message);
      } else {
        alert("Server Error. Please check your backend.");
      }
    } finally {
      setLoading(false); // Button ki loading band karo
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-tr from-slate-50 via-gray-100 to-zinc-50 flex font-sans antialiased">
      
      {/* Left Section (Branding & Features) */}
      <div className="hidden lg:flex w-3/5 p-20 flex-col justify-between relative overflow-hidden bg-white border-r border-gray-100">
        <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-100/40 rounded-full blur-3xl -translate-x-10 -translate-y-10" />
        
        <div className="flex items-center gap-4 relative z-10">
          <div className="bg-white p-2.5 rounded-2xl shadow-md border border-gray-100">
            <img src={Logo} alt="KAD Tech HR" className="h-12 w-auto object-contain" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-gray-900">
              KAD TECH <span className="text-[#D6A700]">HR SOLUTIONS</span>
            </h1>
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">
              Recruitment Management Portal
            </p>
          </div>
        </div>

        <div className="my-auto relative z-10 max-w-xl">
          <h2 className="text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.15] mb-6">
            Empowering Businesses <br />
            with the <span className="underline decoration-[#FED547] decoration-4 underline-offset-4">Right Talent.</span>
          </h2>
          <p className="text-gray-500 text-lg mb-10 leading-relaxed">
            Streamline your hiring workflow, manage payroll seamlessly, and discover top-tier talent all under one robust platform.
          </p>

          <div className="grid grid-cols-2 gap-4">
            {[ "Recruitment Solutions", "Payroll Management", "Staffing Services", "Permanent Hiring" ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-gray-50/80 border border-gray-100 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300">
                <CheckCircle2 className="text-[#D6A700] shrink-0" size={20} />
                <span className="font-medium text-gray-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-xs text-gray-400 relative z-10">
          © {new Date().getFullYear()} KAD Tech HR Solutions. All rights reserved.
        </div>
      </div>

      {/* Right Section (Login Form) */}
      <div className="flex w-full lg:w-2/4 justify-center items-center p-6 md:p-12 relative">
        <div className="absolute bottom-10 right-10 w-72 h-72 bg-amber-100/30 rounded-full blur-3xl" />

        <div className="bg-white/80 backdrop-blur-md border border-white/60 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.05)] p-10 w-full max-w-md relative z-10 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)]">
          
          <div className="flex justify-center lg:hidden mb-6">
            <img src={Logo} alt="Logo" className="h-14 w-auto object-contain rounded-xl shadow-sm p-1.5 bg-white border border-gray-100" />
          </div>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">Admin Login</h2>
            <p className="text-sm text-gray-500 mt-2">Secure access to your management dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="relative group">
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 pl-1">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-[#D6A700] transition-colors duration-200" size={18} />
                <input
                  type="email"
                  name="email"
                  placeholder="admin@kadtech.com"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50/50 border border-gray-200 rounded-xl pl-12 pr-4 py-3.5 text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 focus:bg-white focus:border-[#FED547] focus:ring-4 focus:ring-[#FED547]/10"
                />
              </div>
            </div>

            <div className="relative group">
              <div className="flex justify-between items-center mb-2 pl-1">
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500">Password</label>
                <Link to="/admin/forgot-password" className="text-xs font-semibold text-[#D6A700] hover:text-yellow-600 transition-colors">Forgot Password?</Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-[#D6A700] transition-colors duration-200" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-50/50 border border-gray-200 rounded-xl pl-12 pr-12 py-3.5 text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 focus:bg-white focus:border-[#FED547] focus:ring-4 focus:ring-[#FED547]/10"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-600 transition-colors">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FED547] hover:bg-[#ebd052] text-black font-bold rounded-xl py-4 transition-all duration-300 transform active:scale-[0.99] shadow-lg shadow-yellow-400/20 disabled:opacity-70 disabled:pointer-events-none mt-2"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-black" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing In...
                </span>
              ) : (
                "Sign In to Dashboard"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}